// Everything that changes the board goes through commit(), which applies the
// change, records how to undo it, and queues it for the server.

import { arrivedButton, canHaveJob } from "../../shared/brief";
import { SPEC, GRID } from "../../shared/catalog";
import { newId } from "../../shared/ids";
import type { Board, BoardFolder, BoardKind, Entry, ID, InkItem, Item, Op, ThemeSettings, WidgetItem, WidgetType } from "../../shared/types";
import { descendants, starterEntries } from "../../shared/entries";
import { asBody, mergeBlocks, widgetIds } from "../../shared/docblocks";
import { hasMrr, pipelineShelf, shiftData } from "../../shared/pipeline";
import { shiftSheet, wireCells } from "../../shared/sheet/model";
import { wireSlide } from "../../shared/slideDesign";
import { shiftSlide } from "./patches";
import { BOARD_KINDS, kindOf } from "../../shared/boardKinds";
import { KINDS } from "../boards/kinds";
import { defaultShare } from "../../shared/templates";
import { freeName } from "../../shared/boardNames";
import { inListOrder, lineOf, moveLine, nextOrder, numbered, placeAt, placeLoose, topLevel, type ListEntry } from "../../shared/boardsOrder";
import { api } from "../lib/api";
import { DEMO } from "../demo/mode";
import { camera, viewportCenter } from "../canvas/camera";
import { applyOps, enqueue } from "./sync";
import { boardEntries, boardItems, currentBoard, get, set, toast } from "./store";
import { writeStored } from "../lib/stored";

interface Step {
  label: string;
  do: Op[];
  undo: Op[];
  /**
   * The pages the step put, before and after it. Their undo and redo are worked out when they run, on each
   * page as it is then: only what the step changed goes back (or forward), never the writing done since.
   */
  pages?: PageStep[];
  /** a change made outside the board's own data (an event in a Google calendar): what takes it back, and puts it again */
  outside?: { undo: () => unknown; redo: () => unknown };
}

/**
 * A change made outside the board's own data, on the undo stack as a step of its own (Calendar plan, step 3: an event
 * changed in a Google calendar is taken back in Google by Ctrl+Z, as the board's own changes are). Says what takes
 * this step back from a toast's Undo: as Ctrl+Z would when it is the last step; else it alone, taken off the stack;
 * nothing once Ctrl+Z took it back.
 */
export function recordOutside(label: string, outside: NonNullable<Step["outside"]>): () => void {
  const step: Step = { label, do: [], undo: [], outside };
  undoStack.push(step);
  if (undoStack.length > 200) undoStack.shift();
  redoStack.length = 0;
  return () => {
    if (undoStack[undoStack.length - 1] === step) return void undo();
    const i = undoStack.indexOf(step);
    if (i < 0) return;
    undoStack.splice(i, 1);
    void Promise.resolve().then(outside.undo);
  };
}

interface PageStep {
  id: ID;
  /** null: the step made the page */
  before: Entry | null;
  after: Entry;
  /** a page the step made, as its undo took it away with the pages and widgets in it, for redo to bring back */
  gone?: { entries: Entry[]; items: Item[] };
}

/**
 * An entry moved by one step's change, from one state to another, as it is now: its fields the step changed (a
 * page's title and icon), and a page's blocks (shared/docblocks.ts mergeBlocks).
 */
function shiftPage(cur: Entry, from: Entry, to: Entry): Entry {
  const data = { ...cur.data };
  let { parent, order } = cur;
  let changed = false;
  // each field the step changed (a page's title and icon, an idea's words, fold and side, a post's day, status
  // or text), but a page's body, whose blocks merge below
  const same = (a: unknown, b: unknown) => a === b || JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  for (const k of new Set([...Object.keys(from.data), ...Object.keys(to.data)])) {
    if (k === "body") continue;
    if (!same(from.data[k], to.data[k]) && !same(data[k], to.data[k])) {
      data[k] = to.data[k];
      changed = true;
    }
  }
  if (((from.parent ?? null) !== (to.parent ?? null) || from.order !== to.order) && ((parent ?? null) !== (to.parent ?? null) || order !== to.order)) {
    parent = to.parent;
    order = to.order;
    changed = true;
  }
  if (JSON.stringify(from.data.body ?? null) !== JSON.stringify(to.data.body ?? null)) {
    const { result } = mergeBlocks(asBody(from.data.body).content, asBody(cur.data.body).content, asBody(to.data.body).content);
    const body = { type: "doc", content: result };
    if (JSON.stringify(body) !== JSON.stringify(asBody(cur.data.body))) {
      data.body = body;
      changed = true;
    }
  }
  return changed ? { ...cur, parent, order, data } : cur;
}

/** A step's pages taken back (back) or put forward again, as they are now. */
function pageOps(pages: PageStep[], back: boolean): Op[] {
  const s = get();
  const ops: Op[] = [];
  // entries the step made inside another it made (a sheet's tab and its bands) go with that one, in one trash row
  const made = new Set(pages.filter((p) => !p.before).map((p) => p.id));
  for (const p of back ? [...pages].reverse() : pages) {
    const cur = s.entries[p.id];
    if (!p.before) {
      if (back && cur?.parent && made.has(cur.parent) && s.entries[cur.parent]) continue;
      if (back) {
        // a page the step made goes, with what has been written in it, and the pages and widgets in it, for redo
        if (!cur) continue;
        const below = descendants(Object.values(s.entries), p.id);
        const items = [cur, ...below].flatMap((e) => widgetIds(e.data.body)).flatMap((id) => (s.items[id] ? [s.items[id]] : []));
        p.gone = { entries: [cur, ...below], items };
        ops.push({ op: "entry.del", id: p.id }, ...items.map((i): Op => ({ op: "item.del", id: i.id })));
      } else if (!cur) ops.push(...(p.gone ?? { entries: [p.after], items: [] }).entries.map((entry): Op => ({ op: "entry.put", entry })), ...(p.gone?.items ?? []).map((item): Op => ({ op: "item.put", item })));
      continue;
    }
    if (!cur) continue;
    // a page by its blocks; a sheet's tab and band by their cells, sizes and looks; any other entry (a deal) by its
    // fields, and its notes by their ids (shared/pipeline.ts)
    const shift = cur.t === "page" ? shiftPage : cur.t === "cells" || cur.t === "sheet" ? shiftSheet : cur.t === "slide" ? (c: Entry, f: Entry, t: Entry) => shiftSlide(c, f, t, shiftData) : shiftData;
    const next = back ? shift(cur, p.after, p.before) : shift(cur, p.before, p.after);
    if (next !== cur) ops.push({ op: "entry.put", entry: next });
  }
  return ops;
}

/**
 * Ops as they go to the server: a sheet's band changed goes as the cells that changed alone, so a user and an agent
 * writing other cells of it at once both keep theirs (shared/sheet/model.ts). before: the entries before the change.
 */
function onWire(ops: Op[], before: Record<ID, Entry>): Op[] {
  // a slide goes as the fields, boxes and places it changed (S5, shared/slideDesign.ts)
  return ops.map((op) => (op.op === "entry.put" && op.entry.t === "cells" ? { op: "entry.put", entry: wireCells(before[op.entry.id] ?? null, op.entry) } : op.op === "entry.put" && op.entry.t === "slide" ? { op: "entry.put", entry: wireSlide(before[op.entry.id] ?? null, op.entry) } : op));
}

const undoStack: Step[] = [];
const redoStack: Step[] = [];

/** The ops that take a change back, from the state before it: the last op first. */
export function inverse(ops: Op[]): Op[] {
  const s = get();
  const out: Op[] = [];
  for (const op of ops) {
    if (op.op === "item.put") {
      const old = s.items[op.item.id];
      out.unshift(old ? { op: "item.put", item: old } : { op: "item.del", id: op.item.id });
    } else if (op.op === "item.del") {
      const old = s.items[op.id];
      if (old) out.unshift({ op: "item.put", item: old });
    } else if (op.op === "board.put") {
      const old = s.boards.find((b) => b.id === op.board.id) ?? s.archived.find((b) => b.id === op.board.id);
      // a board in no folder says so (null): a board with no word of its folder keeps the one the server has
      // so too its instructions for agents and an agent's draft of them (Features 3): null takes back what the step set
      if (old) out.unshift({ op: "board.put", board: { ...old, folder: old.folder ?? null, instructions: old.instructions ?? null, instructionsDraft: old.instructionsDraft ?? null } });
    } else if (op.op === "entry.put") {
      const old = s.entries[op.entry.id];
      out.unshift(old ? { op: "entry.put", entry: old } : { op: "entry.del", id: op.entry.id });
    } else if (op.op === "entry.del") {
      // the entries inside it went with it: they come back with it, each inside the one it was in
      const old = s.entries[op.id];
      if (old) out.unshift(...[old, ...descendants(Object.values(s.entries), old.id)].map((entry): Op => ({ op: "entry.put", entry })));
    }
  }
  return out;
}

/**
 * What a board kind writes in place of its own stand-in entries (SC: the slide master's pieces are edited as slides
 * that live only in this tab, and written as the deck's own entry: src/boards/slides/master/virtual.ts).
 */
const rewrites: ((ops: Op[]) => Op[])[] = [];
export function registerOpRewrite(fn: (ops: Op[]) => Op[]) {
  if (!rewrites.includes(fn)) rewrites.push(fn);
}

export function commit(ops0: Op[], label = "Edit", opts: { history?: boolean; undo?: Op[] } = {}) {
  let ops = ops0;
  for (const fn of rewrites) ops = fn(ops);
  if (!ops.length) return;
  // an archived board is read only until it is restored (Features 1, 20 Oct): a key, a voice command or a menu
  // that would change it changes nothing, and says why
  if (touchesArchived(ops)) {
    toast({ level: "info", title: "This board is archived", body: "Restore it to change it.", key: "archived" });
    return;
  }
  const stamped = ops.map((op) => (op.op === "item.put" ? { ...op, item: { ...op.item, updatedAt: Date.now() } } : op.op === "entry.put" ? { ...op, entry: { ...op.entry, updatedAt: Date.now(), by: null } } : op));
  if (opts.history !== false) {
    // a page the step puts is taken back by what the step changed in it, when undone (pageOps)
    const s = get();
    const pages: PageStep[] = stamped.flatMap((op) => (op.op === "entry.put" ? [{ id: op.entry.id, before: s.entries[op.entry.id] ?? null, after: op.entry }] : []));
    const mine = new Set(pages.map((p) => p.id));
    const undoOps = (opts.undo ?? inverse(stamped)).filter((op) => !((op.op === "entry.put" && mine.has(op.entry.id)) || (op.op === "entry.del" && mine.has(op.id))));
    undoStack.push({ label, do: stamped, undo: undoOps, ...(pages.length ? { pages } : {}) });
    if (undoStack.length > 200) undoStack.shift();
    redoStack.length = 0;
  }
  const before = get().entries;
  applyOps(stamped);
  enqueue(onWire(stamped, before));
  // what a person just did, for the "did you know" hints (src/help/hints.ts): read there, never kept or sent
  for (const fn of committed)
    try {
      fn(stamped, label, before);
    } catch {
      /* a hint that cannot read the change never stops the change */
    }
}

/** Listeners to the user's own changes as they are made: the hints' only way in. */
export const committed = new Set<(ops: Op[], label: string, before: Record<ID, Entry>) => void>();

/**
 * A change saved in several parts (an import, S3: its pages go to the server in batches) put on the undo stack as one
 * step. Into a board: undo takes back the pages it made at the top, each with the pages inside it, to the trash, and
 * redo puts them back. Boards it made (a doc board, a sheet board, a deck, phase 2): undo deletes them, to the trash.
 */
export function recordMade(label: string, made: { boards: Board[]; roots: Entry[]; all: Entry[] }) {
  const mine = new Set(made.boards.map((b) => b.id));
  const step: Step = {
    label,
    do: [...made.boards.map((board): Op => ({ op: "board.put", board })), ...made.all.filter((e) => mine.has(e.boardId)).map((entry): Op => ({ op: "entry.put", entry }))],
    undo: made.boards.map((b): Op => ({ op: "board.del", id: b.id })),
    // what it put on a board that was there already, taken back page by page
    ...(made.roots.some((e) => !mine.has(e.boardId)) ? { pages: made.roots.filter((e) => !mine.has(e.boardId)).map((e) => ({ id: e.id, before: null, after: e })) } : {}),
  };
  undoStack.push(step);
  if (undoStack.length > 200) undoStack.shift();
  redoStack.length = 0;
}

/** Two values of a board's field alike: an object by its contents, and a field left out the same as one set to null. */
function alike(a: unknown, b: unknown): boolean {
  if ((a ?? null) === (b ?? null)) return true;
  return typeof a === "object" && typeof b === "object" && JSON.stringify(a) === JSON.stringify(b);
}

/**
 * A step's board puts, worked out when its undo or redo runs, on each board as it is then: only the fields the step
 * changed go back (or forward), never what changed since (Fixes 8, from Features 4's instructions check: Ctrl+Z on an
 * older rename put the whole board back as it was then, and an agent's instructions written since went with it). to:
 * the puts that undo or redo would send; from: the step's other side, which says what it changed. A field the step
 * set where there was none goes back as null, which the board takes as cleared.
 */
export function boardStepOps(to: Op[], from: Op[]): Op[] {
  const s = get();
  return to.map((op) => {
    if (op.op !== "board.put") return op;
    const other = from.find((o): o is Extract<Op, { op: "board.put" }> => o.op === "board.put" && o.board.id === op.board.id)?.board;
    const now = s.boards.find((b) => b.id === op.board.id) ?? s.archived.find((b) => b.id === op.board.id);
    if (!other || !now) return op;
    const patch: Record<string, unknown> = {};
    for (const k of new Set([...Object.keys(op.board), ...Object.keys(other)])) {
      if (k === "id" || k === "updatedAt" || k === "instructionsVersions") continue;
      const v = (op.board as unknown as Record<string, unknown>)[k];
      if (!alike(v, (other as unknown as Record<string, unknown>)[k])) patch[k] = v ?? null;
    }
    return { op: "board.put", board: { ...now, ...patch } as Board };
  });
}

/** Takes back the last change; says which (its label, as "Add task"), or null when there was none. */
export function undo(): string | null {
  const e = undoStack.pop();
  if (!e) return null;
  redoStack.push(e);
  if (e.outside) {
    void Promise.resolve().then(e.outside.undo);
    return e.label;
  }
  const ops = [...boardStepOps(e.undo, e.do), ...(e.pages ? pageOps(e.pages, true) : [])];
  const before = get().entries;
  applyOps(ops);
  enqueue(onWire(ops, before));
  return e.label;
}

/** Puts back what undo took; says which (its label), or null when there was nothing to put back. */
export function redo(): string | null {
  const e = redoStack.pop();
  if (!e) return null;
  undoStack.push(e);
  if (e.outside) {
    void Promise.resolve().then(e.outside.redo);
    return e.label;
  }
  const mine = new Set((e.pages ?? []).map((p) => p.id));
  const ops = [...boardStepOps(e.do.filter((op) => !(op.op === "entry.put" && mine.has(op.entry.id))), e.undo), ...(e.pages ? pageOps(e.pages, false) : [])];
  const before = get().entries;
  applyOps(ops);
  enqueue(onWire(ops, before));
  return e.label;
}

/** Change an item locally without saving or history: used while dragging. */
export function transient(patches: Record<ID, Partial<Item>>) {
  set((s) => {
    const items = { ...s.items };
    for (const id in patches) if (items[id]) items[id] = { ...items[id], ...patches[id] } as Item;
    return { items };
  });
}

export function updateItem(id: ID, patch: Partial<WidgetItem> | Partial<InkItem>, label = "Edit") {
  const it = get().items[id];
  if (!it) return;
  commit([{ op: "item.put", item: { ...it, ...patch } as Item }], label);
}

export function updateProps(id: ID, props: Record<string, unknown>, label = "Edit") {
  const it = get().items[id];
  if (!it || it.kind !== "widget") return;
  commit([{ op: "item.put", item: { ...it, props: { ...it.props, ...props } } }], label);
}

function topZ(boardId: ID): number {
  return boardItems(get(), boardId).reduce((m, i) => Math.max(m, i.z), 0);
}

export function freeSpotNear(w: number, h: number, near?: { x: number; y: number }): { x: number; y: number } {
  const s = get();
  const items = boardItems(s).filter((i) => i.kind === "widget");
  const c = near ?? viewportCenter();
  const start = { x: Math.round((c.x - w / 2) / GRID) * GRID, y: Math.round((c.y - h / 2) / GRID) * GRID };
  const gap = 24;
  const hits = (x: number, y: number) => items.some((i) => x < i.x + i.w + gap && x + w + gap > i.x && y < i.y + i.h + gap && y + h + gap > i.y);
  if (!hits(start.x, start.y)) return start;
  // spiral outwards from the middle of the view
  for (let r = 1; r < 60; r++) {
    for (let a = 0; a < 8 * r; a++) {
      const ang = (a / (8 * r)) * Math.PI * 2;
      const x = Math.round((start.x + Math.cos(ang) * r * 48) / GRID) * GRID;
      const y = Math.round((start.y + Math.sin(ang) * r * 36) / GRID) * GRID;
      if (!hits(x, y)) return { x, y };
    }
  }
  return start;
}

export function addWidget(type: WidgetType, opts: { title?: string; stream?: string | null; props?: Record<string, unknown>; at?: { x: number; y: number }; w?: number; h?: number; style?: WidgetItem["style"]; select?: boolean } = {}): WidgetItem | null {
  const s = get();
  const board = currentBoard(s);
  if (!board) return null;
  const spec = SPEC[type];
  const w = opts.w ?? spec.size[0];
  const h = opts.h ?? spec.size[1];
  const spot = opts.at ? { x: Math.round(opts.at.x / GRID) * GRID, y: Math.round(opts.at.y / GRID) * GRID } : freeSpotNear(w, h);
  const item: WidgetItem = {
    id: newId("w_"),
    boardId: board.id,
    kind: "widget",
    type,
    title: opts.title ?? spec.label,
    x: spot.x,
    y: spot.y,
    w,
    h,
    z: topZ(board.id) + 1,
    props: { ...structuredClone(spec.props), ...(opts.props ?? {}) },
    stream: opts.stream ?? null,
    style: opts.style ?? {},
    createdAt: Date.now(),
    updatedAt: Date.now(),
    by: null,
  };
  // a typed board says where a widget goes on it (in a page, on a doc board), or why it cannot go there yet
  const kind = kindOf(board);
  if (kind !== "canvas") {
    const placed = KINDS[kind]?.place?.(item, board) ?? { refused: { title: BOARD_KINDS[kind].noWidgets ?? "Widgets cannot go on this board yet" } };
    if ("refused" in placed) {
      toast({ level: "info", title: placed.refused.title, body: placed.refused.body ?? null });
      return null;
    }
    commit(placed.ops, `Add ${spec.label}`);
    // one that went on the board's canvas (beside a mind map) is shown and set up as on a canvas
    if (placed.onCanvas) {
      if (opts.select !== false) set({ selection: [item.id], tool: "select", inspect: true });
      const put = placed.ops.find((o): o is Extract<Op, { op: "item.put" }> => o.op === "item.put" && o.item.id === item.id)?.item ?? item;
      camera.ensureVisible({ x: put.x, y: put.y, w: put.w, h: put.h });
    }
    return item;
  }
  commit([{ op: "item.put", item }], `Add ${spec.label}`);
  // a widget the user adds opens on its settings, to be set up
  if (opts.select !== false) set({ selection: [item.id], tool: "select", inspect: true });
  // bring it into view if it landed off screen
  camera.ensureVisible({ x: item.x, y: item.y, w: item.w, h: item.h });
  return item;
}

export function addInk(ink: InkItem["ink"], rect: { x: number; y: number; w: number; h: number }, select = false): InkItem | null {
  const board = currentBoard(get());
  if (!board) return null;
  const item: InkItem = { id: newId("i_"), boardId: board.id, kind: "ink", ...rect, z: topZ(board.id) + 1, ink, createdAt: Date.now(), updatedAt: Date.now() };
  commit([{ op: "item.put", item }], "Draw");
  if (select) set({ selection: [item.id] });
  return item;
}

export function deleteItems(ids: ID[]) {
  const s = get();
  const ops: Op[] = ids.filter((id) => s.items[id] && !s.items[id].locked).map((id) => ({ op: "item.del", id }));
  if (!ops.length) return;
  commit(ops, ops.length === 1 ? "Delete" : `Delete ${ops.length}`);
  set({ selection: [] });
  toast({ level: "info", title: ops.length === 1 ? "Removed" : `Removed ${ops.length} items`, action: { label: "Undo", run: undo } });
}

export function duplicateItems(ids: ID[], offset = 24): ID[] {
  const s = get();
  const z0 = topZ(s.boardId!);
  const ops: Op[] = [];
  const created: ID[] = [];
  ids.forEach((id, n) => {
    const it = s.items[id];
    if (!it) return;
    const copy = { ...structuredClone(it), id: newId(it.kind === "widget" ? "w_" : "i_"), x: it.x + offset, y: it.y + offset, z: z0 + n + 1, createdAt: Date.now(), updatedAt: Date.now(), locked: undefined, by: null } as Item;
    // a widget's job stays with the original, which fills the stream they share; a second would run it twice
    if (copy.kind === "widget" && canHaveJob(copy.type)) delete copy.props.auto;
    // a button, job or brief runs hands-free only where the user turned that on: never a copy of it, nor a button's
    // sends (Round 7, Features 4)
    if (copy.kind === "widget" && (copy.type === "action" || copy.type === "job" || copy.type === "brief") && copy.props.auto && typeof copy.props.auto === "object") {
      delete (copy.props.auto as { noAsk?: true }).noAsk;
      delete (copy.props.auto as { noAskSend?: true }).noAskSend;
    }
    ops.push({ op: "item.put", item: copy });
    created.push(copy.id);
  });
  commit(ops, "Duplicate");
  set({ selection: created });
  return created;
}

export function bringToFront(ids: ID[]) {
  const s = get();
  let z = topZ(s.boardId!);
  commit(
    ids.map((id) => ({ op: "item.put", item: { ...s.items[id], z: ++z } as Item })),
    "Bring to front",
  );
}

export function sendToBack(ids: ID[]) {
  const s = get();
  let z = boardItems(s).reduce((m, i) => Math.min(m, i.z), 0);
  commit(
    ids.map((id) => ({ op: "item.put", item: { ...s.items[id], z: --z } as Item })),
    "Send to back",
  );
}

export function toggleLock(ids: ID[]) {
  const s = get();
  const anyUnlocked = ids.some((id) => !s.items[id]?.locked);
  commit(
    ids.map((id) => ({ op: "item.put", item: { ...s.items[id], locked: anyUnlocked ? true : undefined } as Item })),
    anyUnlocked ? "Lock" : "Unlock",
  );
}

export function nudge(ids: ID[], dx: number, dy: number) {
  const s = get();
  commit(
    ids.filter((id) => !s.items[id]?.locked).map((id) => ({ op: "item.put", item: { ...s.items[id], x: s.items[id].x + dx, y: s.items[id].y + dy } as Item })),
    "Move",
  );
}

// ---------------------------------------------------------------------------
// boards

export function switchBoard(id: ID) {
  // only to a board that is there: one deleted in another tab or on another device a moment ago would leave nothing
  // to show. An archived one opens read only, from Archived
  if (!get().boards.some((b) => b.id === id) && !get().archived.some((b) => b.id === id)) return;
  set({ boardId: id, selection: [], editing: null });
  writeStored("cb:board", id);
  // when, so a first load opens the newer of this and the account's last place (src/help/leftOff.ts)
  writeStored("cb:board:at", String(Date.now()));
  camera.restore(id);
}

export function updateBoard(patch: Partial<Board>, label = "Board") {
  const b = currentBoard(get());
  if (!b) return;
  commit([{ op: "board.put", board: { ...b, ...patch } }], label);
}

export function updateTheme(patch: Partial<ThemeSettings>) {
  const b = currentBoard(get());
  if (!b) return;
  commit([{ op: "board.put", board: { ...b, theme: { ...b.theme, ...patch } } }], "Theme");
}

/**
 * A new board, opened unless open is false (the overview opens it itself, once it has zoomed in). Its kind
 * is chosen here, once: a canvas is left without one, as boards always were, and a doc board starts on a page.
 */
export function createBoard(name = "New board", theme: ThemeSettings = { id: "studio" }, open = true, kind: BoardKind = "canvas"): Board {
  const s = get();
  const b: Board = {
    id: newId("b_"),
    name,
    emoji: null,
    // at the foot of the list, under every board and folder (Round 6)
    order: nextOrder(s.workspace?.folders ?? [], s.boards),
    theme,
    share: defaultShare(),
    createdAt: Date.now(),
    updatedAt: Date.now(),
    ...(kind !== "canvas" ? { kind } : {}),
    // a new Files board asks for its folder before it shows any file (Fixes 9)
    ...(kind === "files" ? { filesAsk: true } : {}),
  };
  const first = starterEntries(b, Date.now(), () => newId("e_"), Intl.DateTimeFormat().resolvedOptions().timeZone || null, typeof navigator !== "undefined" ? navigator.language || null : null);
  // a client pipeline's shelf: what is open, what was won this quarter, and MRR when the account's mrr has data
  const shelf = kind === "pipeline" ? pipelineShelf(b.id, Date.now(), () => newId("w_"), hasMrr(s.streams.mrr)) : [];
  commit([{ op: "board.put", board: b }, ...first.map((entry): Op => ({ op: "entry.put", entry })), ...shelf.map((item): Op => ({ op: "item.put", item }))], "New board", { history: false });
  if (open) switchBoard(b.id);
  return b;
}

// ---------------------------------------------------------------------------
// entries: what a typed board holds of its own (a doc board's pages)

/** Makes or changes entries in one step, which one undo takes back. */
export function putEntries(entries: Entry[], label = "Edit") {
  commit(
    entries.map((entry): Op => ({ op: "entry.put", entry })),
    label,
  );
}

/** An entry, and every entry inside it, to the trash, with Undo. */
export function deleteEntry(id: ID, what = "page") {
  const e = get().entries[id];
  if (!e) return;
  const below = descendants(boardEntries(get(), e.boardId), id);
  // what it holds of its own kind: a plan's questions inside a page are not pages inside it
  const inside = below.filter((x) => x.t === e.t).length;
  // the widgets its pages hold go with them, as on the server, and one undo brings them all back
  const widgets = [e, ...below].flatMap((x) => (x.t === "page" ? widgetIds(x.data.body) : [])).filter((w) => get().items[w]);
  commit([{ op: "entry.del", id }, ...widgets.map((w): Op => ({ op: "item.del", id: w }))], `Delete ${what}`);
  toast({ level: "info", title: inside ? `Moved the ${what} and ${inside === 1 ? `the ${what} inside it` : `${inside} ${what}s inside it`} to the trash` : `Moved the ${what} to the trash`, action: { label: "Undo", run: undo } });
}

/**
 * A new board's name when the user typed none (Fixes 9): its kind's or template's own name, made free of every board's,
 * archived ones too, so a second Content calendar is "Content calendar 2".
 */
export function newBoardName(base: string): string {
  const s = get();
  return freeName(base, [...s.boards, ...s.archived].map((b) => b.name));
}

/** A canvas from a template; name is what the user typed, else the template's own, made free of the others' (the server's). */
export async function createBoardFromTemplate(template: string, open = true, name?: string): Promise<ID> {
  const r = await api<{ boardId: string }>("/api/boards/from-template", { body: { template, ...(name ? { name } : {}) } });
  const { resync } = await import("./sync");
  await resync();
  if (open) switchBoard(r.boardId);
  return r.boardId;
}

/**
 * A Custom board copied as a board of its own, opened once it is there: Duplicate (records true) is its app and its
 * records, New board from this its app alone. The server copies, so the copy shares nothing with the original.
 */
/** A Custom board copied: its app and records (Duplicate), or its app alone (New board from this). name: the one typed in
 * New board; open: whether it opens here (New board from All boards zooms into it itself). The new board's id, or null. */
export async function copyBoard(id: ID, records: boolean, opts: { name?: string; open?: boolean } = {}): Promise<ID | null> {
  const { flush, resync } = await import("./sync");
  // what was just typed into the original reaches the server first, so a duplicate holds it
  await flush().catch(() => null);
  try {
    const r = await api<{ boardId: string }>(`/api/boards/${encodeURIComponent(id)}/copy`, { body: { records, ...(opts.name ? { name: opts.name } : {}) } });
    await resync();
    if (opts.open !== false) switchBoard(r.boardId);
    const made = get().boards.find((b) => b.id === r.boardId);
    toast({ level: "success", title: made ? `Made ${made.name}` : "Made the board", body: records ? "Its app and its records, copied." : "The same app, with no records." });
    return r.boardId;
  } catch (e) {
    toast({ level: "error", title: records ? "Could not duplicate it" : "Could not make a new board", body: (e as Error).message });
    return null;
  }
}

export function deleteBoard(id: ID) {
  const s = get();
  // an archived board goes to the trash as any does; the last one in use stays
  if (!s.archived.some((b) => b.id === id) && s.boards.length <= 1) {
    toast({ level: "warn", title: "You need at least one board" });
    return;
  }
  const wasOpen = s.boardId === id;
  const name = (s.boards.find((b) => b.id === id) ?? s.archived.find((b) => b.id === id))?.name;
  commit([{ op: "board.del", id }], "Delete board", { history: false });
  // with Undo (Fixes 9): the board back from the trash whole, and open again if it was
  // (the demo keeps no trash, so it has no Undo)
  toast({ level: "info", title: name ? `Deleted ${name}` : "Board deleted", body: "It is in the trash for 30 days, under Settings.", key: `delete:${id}`, ...(DEMO ? {} : { action: { label: "Undo", run: () => void undeleteBoard(id, wasOpen) } }) });
}

/** A deleted board back from the trash, whole, as Settings' Trash brings it: once the delete has reached the account. */
export async function undeleteBoard(id: ID, open = false) {
  const { flush, resync } = await import("./sync");
  await flush().catch(() => null);
  try {
    await api(`/api/trash/${encodeURIComponent(id)}/restore`, { body: {} });
  } catch (e) {
    const gone = /not in the trash/i.test((e as Error).message);
    toast({ level: "error", title: "Could not bring it back", body: gone ? "It is no longer in the trash: a deleted board is kept there for 30 days." : `${(e as Error).message} It is still in the trash, under Settings.` });
    return;
  }
  await resync();
  if (open) switchBoard(id);
  toast({ level: "success", title: "Brought it back", body: "Back in your boards list, as it was.", key: `delete:${id}` });
}

/** Whether a change would touch an archived board's things, or the board itself other than restoring it. */
function touchesArchived(ops: Op[]): boolean {
  const s = get();
  if (!s.archived.length) return false;
  const shelved = new Set(s.archived.map((b) => b.id));
  const on = (id: ID | undefined) => !!id && shelved.has(id);
  return ops.some((op) =>
    op.op === "item.put" ? on(op.item.boardId) || on(s.items[op.item.id]?.boardId) : op.op === "item.del" ? on(s.items[op.id]?.boardId) : op.op === "entry.put" ? on(op.entry.boardId) : op.op === "entry.del" ? on(s.entries[op.id]?.boardId) : op.op === "board.put" ? on(op.board.id) && !!op.board.archived : false,
  );
}

/** When a board was archived, as its bar and Archived say it: "20 Oct", or "20 Oct 2025" in another year. */
export function archivedOn(t: number): string {
  const d = new Date(t);
  const same = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", ...(same ? {} : { year: "numeric" }) });
}

/** How many boards an account keeps archived: the board's own cap (worker/workspace.ts MAX_ARCHIVED). */
export const MAX_ARCHIVED = 200;

/**
 * Archive a board the user is done with (Features 1, 20 Oct): out of the boards list, kept whole and read only,
 * nothing on it running, until Restore. The one in view gives way to the next, and a receipt says where it went,
 * with Undo.
 */
export function archiveBoard(id: ID) {
  const s = get();
  const b = s.boards.find((x) => x.id === id);
  if (!b) return;
  if (s.boards.length <= 1) {
    toast({ level: "warn", title: "You need at least one board that isn't archived" });
    return;
  }
  if (s.archived.length >= MAX_ARCHIVED) {
    toast({ level: "warn", title: `Up to ${MAX_ARCHIVED} archived boards`, body: "Delete one you no longer need from Archived first." });
    return;
  }
  const wasOpen = s.boardId === id;
  if (wasOpen) {
    const i = s.boards.indexOf(b);
    const home = homeBoard(s);
    switchBoard((home && home.id !== id ? home : (s.boards[i + 1] ?? s.boards[i - 1])).id);
  }
  commit([{ op: "board.put", board: { ...b, archived: Date.now() } }], "Archive board", { history: false });
  toast({
    level: "success",
    title: `Archived ${b.name}`,
    body: "It's under Archived, at the end of your boards list and All boards.",
    key: `archive:${id}`,
    action: {
      label: "Undo",
      run: () => {
        restoreBoard(id, { quiet: true });
        if (wasOpen) switchBoard(id);
      },
    },
  });
}

/** An archived board back in the boards list, exactly as it was; its folder, when that folder is still there. */
export function restoreBoard(id: ID, opts: { quiet?: boolean } = {}) {
  const s = get();
  const b = s.archived.find((x) => x.id === id);
  if (!b) return;
  if (s.boards.length >= (s.limits?.boards ?? Infinity)) {
    toast({ level: "warn", title: `Up to ${s.limits!.boards} boards in your list`, body: "Archive or delete one, then restore this one." });
    return;
  }
  const folder = b.folder && (s.workspace?.folders ?? []).some((f) => f.id === b.folder) ? b.folder : null;
  commit([{ op: "board.put", board: { ...b, archived: null, folder } }], "Restore board", { history: false });
  if (!opts.quiet) toast({ level: "success", title: `Restored ${b.name}`, body: "It's back in your boards list, as it was.", key: `archive:${id}` });
}

/** Changes any board, not only the one shown: its name or emoji, from the board menu. */
export function updateBoardById(id: ID, patch: Partial<Pick<Board, "name" | "emoji">>, label = "Board") {
  const b = get().boards.find((x) => x.id === id);
  if (!b) return;
  commit([{ op: "board.put", board: { ...b, ...patch } }], label);
}

// ---------------------------------------------------------------------------
// the home board (David, 17 Oct): the one the Home button, top left, goes back to

/** The home board, while it is still there. */
export function homeBoard(s = get()): Board | null {
  const id = s.workspace?.home;
  return (id && s.boards.find((b) => b.id === id)) || null;
}

/** Makes a board home, or none with null; kept on the account, so every device has the same one. */
export function setHome(id: ID | null) {
  if ((get().workspace?.home ?? null) === id) return;
  commit([{ op: "workspace.put", patch: { home: id } }], id ? "Home board" : "No home board", { history: false });
}

/** The boards as tabs across the top instead of the pill (Features 7); kept on the account, so every device shows the same. */
export function setBoardTabs(on: boolean) {
  if (!!get().workspace?.boardTabs === on) return;
  commit([{ op: "workspace.put", patch: { boardTabs: on ? true : null } }], on ? "Boards as tabs" : "Boards in the pill", { history: false });
}

/** How the tabs look (Features 8): floating where the pill sits or a bar across the top, with the boards' icons or without; kept on the account too. */
export function setTabLook(change: { bar?: boolean; icons?: boolean }) {
  const was = get().workspace?.tabLook ?? {};
  const bar = change.bar ?? !!was.bar;
  const noIcons = change.icons === undefined ? !!was.noIcons : !change.icons;
  const next = bar || noIcons ? { ...(bar ? { bar: true as const } : {}), ...(noIcons ? { noIcons: true as const } : {}) } : null;
  if (!!was.bar === bar && !!was.noIcons === noIcons) return;
  commit([{ op: "workspace.put", patch: { tabLook: next } }], "Tabs' look", { history: false });
}

/** X Premium (Fixes 10): the 280 character warning on X posts goes; kept on the account, so every device agrees. */
export function setXPremium(on: boolean) {
  if (!!get().workspace?.xPremium === on) return;
  commit([{ op: "workspace.put", patch: { xPremium: on ? true : null } }], on ? "X Premium" : "No X Premium", { history: false });
}

// ---------------------------------------------------------------------------
// folders in the boards list (David, 16 Oct): one level, named, a board in one or none

export function folders(): BoardFolder[] {
  return get().workspace?.folders ?? [];
}

/** A new folder at the foot of the list, empty; its id, to rename it at once. */
export function addFolder(name = "New folder"): ID {
  const id = newId("f_").replace(/[^\w-]/g, "").slice(0, 30);
  commit([{ op: "workspace.put", patch: { folders: [...folders(), { id, name, order: nextOrder(folders(), get().boards) }] } }], "New folder", { history: false });
  return id;
}

/**
 * The boards list as it should now stand (shared/boardsOrder.ts): every board and folder takes its place's number,
 * and a board its folder, in one batch, so a board can stand above the folders and stay there on every device.
 */
export function arrangeList(entries: ListEntry[], label = "Reorder boards") {
  const s = get();
  const n = numbered(entries);
  const ops: Op[] = [];
  for (const b of s.boards) {
    const at = n.boards.get(b.id);
    if (at && (at.order !== b.order || at.folder !== (b.folder ?? null))) ops.push({ op: "board.put", board: { ...b, order: at.order, folder: at.folder } });
  }
  const was = folders();
  if (JSON.stringify(n.folders) !== JSON.stringify(was)) ops.unshift({ op: "workspace.put", patch: { folders: n.folders } });
  if (ops.length) commit(ops, label, { history: false });
}

/** The boards list's top level as it shows now. */
export function listNow(): ListEntry[] {
  return topLevel(folders(), get().boards);
}

/** A folder, or a board in no folder, one place up or down the list's top level, past a whole folder at a time. */
export function moveLineBy(ref: { board?: ID; folder?: ID }, dir: -1 | 1) {
  const list = listNow();
  const i = lineOf(list, ref);
  if (i < 0 || i + dir < 0 || i + dir >= list.length) return;
  arrangeList(moveLine(list, i, i + dir));
}

/** A folder dragged to a top-level place: before the line at that place when it comes from below, after it from above. */
export function placeFolder(id: ID, target: { board?: ID; folder?: ID }) {
  const list = listNow();
  const from = lineOf(list, { folder: id });
  const to = lineOf(list, target);
  if (from < 0 || to < 0 || from === to) return;
  arrangeList(moveLine(list, from, to));
}

/** A board dropped on another's row: it takes that place and that board's folder, or none (shared placeAt). */
export function dropBoard(id: ID, target: ID) {
  arrangeList(placeAt(listNow(), id, target), "Move board");
}

/** A board dropped just above a folder: out of any folder, standing above it. */
export function placeAboveFolder(id: ID, folder: ID) {
  const list = listNow();
  const at = lineOf(list, { folder });
  if (at >= 0) arrangeList(placeLoose(list, id, at), "Move board");
}

export function renameFolder(id: ID, name: string) {
  const clean = name.replace(/\s+/g, " ").trim().slice(0, 40);
  if (!clean) return;
  commit([{ op: "workspace.put", patch: { folders: folders().map((f) => (f.id === id ? { ...f, name: clean } : f)) } }], "Rename folder", { history: false });
}

/** A folder goes; its boards never do: they go back to the loose list. */
export function deleteFolder(id: ID) {
  const loose = get().boards.filter((b) => b.folder === id).map((b) => ({ op: "board.put" as const, board: { ...b, folder: null } }));
  commit([{ op: "workspace.put", patch: { folders: folders().filter((f) => f.id !== id) } }, ...loose], "Delete folder", { history: false });
}

/** A board into a folder, or out of every folder with null. */
export function setBoardFolder(boardId: ID, folder: ID | null) {
  const b = get().boards.find((x) => x.id === boardId);
  if (!b || (b.folder ?? null) === folder) return;
  // out of a folder is null, said out loud: a board with no word of its folder keeps the one the server has
  commit([{ op: "board.put", board: { ...b, folder } }], folder ? "Move board to folder" : "Take board out of folder");
}

/**
 * A board dragged to a new place among all the boards (All boards): it takes that place and keeps its folder, the
 * boards between shift along one, and the folders keep their places among the boards in none (Round 6).
 */
export function placeBoard(id: ID, to: number) {
  const s = get();
  const seq = inListOrder(folders(), s.boards);
  const from = seq.findIndex((b) => b.id === id);
  if (from < 0 || to < 0 || to >= seq.length || from === to) return;
  const [moved] = seq.splice(from, 1);
  seq.splice(to, 0, moved);
  const rank = new Map(seq.map((b, i) => [b.id, i]));
  const by = (a: Board, b: Board) => rank.get(a.id)! - rank.get(b.id)!;
  const list = listNow();
  const loose = list.flatMap((e) => ("board" in e ? [e.board] : [])).sort(by);
  let k = 0;
  arrangeList(list.map((e) => ("folder" in e ? { folder: e.folder, boards: [...e.boards].sort(by) } : { board: loose[k++] })));
}

export function cycleBoard(dir: -1 | 1) {
  const s = get();
  // the list's order, which is the tabs' order: a folder's boards where the folder stands
  const seq = inListOrder(folders(), s.boards);
  const i = seq.findIndex((b) => b.id === s.boardId);
  const next = seq[(i + dir + seq.length) % seq.length];
  if (next) switchBoard(next.id);
}

// ---------------------------------------------------------------------------
// clipboard (within the app, and as JSON on the system clipboard)

let clip: Item[] = [];

export function copyItems(ids: ID[]) {
  const s = get();
  clip = ids.map((id) => s.items[id]).filter(Boolean) as Item[];
  try {
    void navigator.clipboard?.writeText(JSON.stringify({ contextBoard: 1, items: clip }));
  } catch {
    /* the in-app copy is enough */
  }
}

export function pasteItems(source?: Item[]) {
  const s = get();
  const items = source ?? clip;
  if (!items.length || !s.boardId) return;
  const minX = Math.min(...items.map((i) => i.x));
  const minY = Math.min(...items.map((i) => i.y));
  const c = viewportCenter();
  const w = Math.max(...items.map((i) => i.x + i.w)) - minX;
  const h = Math.max(...items.map((i) => i.y + i.h)) - minY;
  const dx = Math.round((c.x - w / 2 - minX) / GRID) * GRID;
  const dy = Math.round((c.y - h / 2 - minY) / GRID) * GRID;
  let z = topZ(s.boardId);
  // a pasted button asks before its first run, as one an agent set up does: what the clipboard holds may not be the user's
  const ops: Op[] = items.map((it) => ({
    op: "item.put",
    item: arrivedButton({ ...structuredClone(it), id: newId(it.kind === "widget" ? "w_" : "i_"), boardId: s.boardId!, x: it.x + dx, y: it.y + dy, z: ++z, locked: undefined, by: null } as Item),
  }));
  commit(ops, "Paste");
  set({ selection: ops.map((o) => (o as { item: Item }).item.id) });
}
